import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor } from '@repo/tailwind-config/tailwind.vars'
import { AvailableLanguages, languages } from '@repo/toolkit/language-types'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import { ChevronDownIcon } from '@repo/ui/icons'
import Popover from '@repo/ui/popover'
import Typography from '@repo/ui/typography'
import { useState } from 'react'

type Props = {
  language: AvailableLanguages
  onLanguageChange: (language: AvailableLanguages) => void
  color: TailwindColor
}

const TranslationDropdown: React.FC<Props> = ({ language, onLanguageChange, color }) => {
  const [open, setOpen] = useState(false)
  const [focusedLanguage, setFocusedLanguage] = useState<AvailableLanguages>(language) // Manage focus state
  const breakpoint = useBreakPoint()
  const isMobile = breakpoint === 'mobile'

  const languagesArray = Object.values(languages)
  const currentIndex = languagesArray.indexOf(focusedLanguage)

  const handleKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
    if (event.key === 'ArrowDown') {
      const nextIndex = (currentIndex + 1) % languagesArray.length
      setFocusedLanguage(languagesArray[nextIndex])
      event.preventDefault()
    } else if (event.key === 'ArrowUp') {
      const prevIndex = (currentIndex - 1 + languagesArray.length) % languagesArray.length
      setFocusedLanguage(languagesArray[prevIndex])
      event.preventDefault()
    } else if (event.key === 'Enter') {
      if (focusedLanguage) {
        onLanguageChange(focusedLanguage)
        setOpen(false)
      }
    }
  }

  return (
    <Popover
      isOpen={open}
      onOpenChange={setOpen}
      className="min-w-40 z-50"
      triggerComponent={
        <button className="flex flex-row items-center gap-2.5 outline-none">
          <div className="flex flex-row items-center gap-1">
            <Typography variant="tag" className="select-none" color={color}>
              {language.toUpperCase()}
            </Typography>
            <ChevronDownIcon
              color={color}
              className={cn(
                'transition-transform duration-300 mt-1',
                open && 'transform rotate-180'
              )}
            />
          </div>
        </button>
      }
      position={isMobile ? 'end' : 'start'}
    >
      <ul className="flex flex-col gap-1 p-2">
        {languagesArray.map((lang, index) => {
          return (
            <li
              key={lang}
              className={cn(
                'flex flex-row items-center gap-2.5 outline-none p-2 rounded duration-300',
                {
                  'hover:transform hover:scale-105': true,
                  'bg-grey-light transform scale-100': lang === focusedLanguage
                }
              )}
              onClick={() => {
                onLanguageChange(lang)
                setFocusedLanguage(lang)
                setOpen(false)
              }}
              onKeyDown={handleKeyDown}
              role="button"
              tabIndex={index}
            >
              <Typography variant="tag">{lang.toUpperCase()}</Typography>
            </li>
          )
        })}
      </ul>
    </Popover>
  )
}

export default TranslationDropdown
